/**
 * SCM「人工粘贴解析」面板
 * 粘贴 SCM 报文文本 → 调后端解析+比对 → 展示结果
 * 同一指纹重复粘贴：以最后一次为准覆盖更新已有待审记录
 */

import React, { useState } from 'react';
import { Card, Input, Button, Space, Alert, message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { scmService } from '../../../../api/enhancedApi';

const { TextArea } = Input;

const ScmInboundParsePanel = ({ onParsed }) => {
    const [raw, setRaw] = useState('');
    const [parsing, setParsing] = useState(false);
    const [result, setResult] = useState(null);

    const handleParse = async () => {
        if (!raw.trim()) {
            message.warning('请先粘贴 SCM 报文文本');
            return;
        }
        setParsing(true);
        setResult(null);
        try {
            const resp = await scmService.parseScmInbound({ raw_message: raw });
            const data = resp.data || {};
            setResult(data);
            const hasCreated = data.created && data.created.length > 0;
            const hasUpdated = data.updated && data.updated.length > 0;
            const hasDuplicates = data.duplicates && data.duplicates.length > 0;
            if (hasCreated || hasUpdated) {
                message.success(data.message || (hasUpdated ? '已按最新粘贴覆盖更新' : '解析成功，已生成待审比对记录'));
                onParsed && onParsed();
            } else if (data.parse_failed) {
                message.error('解析失败，请检查报文格式');
            } else if (hasDuplicates) {
                message.info(data.message || '该报文已存在');
            } else {
                message.info(data.message || '未识别到 SCM 报文段');
            }
        } catch (error) {
            message.error(error.response?.data?.detail || '解析失败');
        } finally {
            setParsing(false);
        }
    };

    const handleClear = () => {
        setRaw('');
        setResult(null);
    };

    const hasCreated = result?.created?.length > 0;
    const hasUpdated = result?.updated?.length > 0;
    const hasDuplicates = result?.duplicates?.length > 0;

    return (
        <Card size="small" title="人工粘贴解析" style={{ marginBottom: 16 }}>
            <TextArea
                rows={6}
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="把 SCM 报文粘贴到这里（含站点行、类型段、ULD 清单）"
                style={{ fontFamily: 'Courier New, monospace' }}
            />
            <Space style={{ marginTop: 12 }}>
                <Button type="primary" icon={<ThunderboltOutlined />} loading={parsing} onClick={handleParse}>
                    解析比对
                </Button>
                <Button onClick={handleClear} disabled={parsing}>清空</Button>
            </Space>

            {result && (hasCreated || hasUpdated) && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="success"
                    showIcon
                    message={result.message || (hasUpdated ? '已按最新粘贴覆盖更新待审比对记录' : '解析成功，已生成待审比对记录')}
                />
            )}

            {result && result.parse_failed && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="error"
                    showIcon
                    message="未能从报文中解析出有效站点/ULD 清单"
                    description="请核对是否为标准 SCM 报文格式"
                />
            )}

            {result && hasDuplicates && !hasCreated && !hasUpdated && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message={result.message || '该报文已存在'}
                />
            )}

            {result && !result.parse_failed && !hasCreated && !hasUpdated && !hasDuplicates && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="info"
                    showIcon
                    message={result.message || '未识别到 SCM 报文段，未生成记录'}
                />
            )}
        </Card>
    );
};

export default ScmInboundParsePanel;
